import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCircle2, AlertCircle, Clock, CheckCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import EmptyState from "@/components/EmptyState";
import { fmtDate } from "@/lib/format";
import api from "@/lib/api";

function NoteIcon({ type }) {
  if (type === "acknowledgement") return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
  if (type === "dispute") return <AlertCircle className="h-5 w-5 text-amber-600" />;
  if (type === "overdue") return <AlertCircle className="h-5 w-5 text-red-600" />;
  return <Clock className="h-5 w-5 text-primary" />;
}

export default function Notifications() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
  });

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notifications-count"] });
  };

  const markRead = async (id, debtId, link) => {
    await api.patch(`/notifications/${id}/read`).catch(() => {});
    refetch();
    if (link) navigate(link);
    else if (debtId) navigate(`/debt/${debtId}`);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    refetch();
  };

  const notes = data?.notifications || [];

  return (
    <div className="pb-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-foreground">Notifications</h1>
        {notes.some((n) => !n.read) && (
          <button data-testid="mark-all-read" onClick={markAll} className="text-sm font-semibold text-accent inline-flex items-center gap-1">
            <CheckCheck className="h-4 w-4" /> Mark all read
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2.5">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}</div>
      ) : notes.length === 0 ? (
        <EmptyState testId="notifications-empty" icon={Bell} title="You're all caught up" subtitle="Reminders and acknowledgement updates will show up here." />
      ) : (
        <div className="mt-4 space-y-2.5">
          {notes.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-${n.id}`}
              onClick={() => markRead(n.id, n.debt_id, n.link)}
              className={`w-full text-left rounded-2xl border p-4 flex items-start gap-3 transition-colors ${
                n.read ? "bg-card border-border" : "bg-emerald-50/60 border-emerald-200"
              }`}
            >
              <div className="h-9 w-9 shrink-0 rounded-full bg-secondary flex items-center justify-center mt-0.5">
                <NoteIcon type={n.type} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{n.title}</p>
                <p className="text-sm text-muted-foreground">
                  {n.dispute_comment ? n.message.split(" They said:")[0] : n.message}
                </p>
                {n.dispute_comment && (
                  <p data-testid={`notification-comment-${n.id}`} className="mt-1.5 rounded-lg border-l-2 border-amber-400 bg-amber-50 px-3 py-1.5 text-sm italic text-foreground">
                    {"\u201C"}{n.dispute_comment}{"\u201D"}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">{fmtDate(n.created_at)}</p>
              </div>
              {!n.read && <span className="h-2 w-2 rounded-full bg-accent mt-2" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
